import { useEffect, useState } from 'react';

import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

export type PipelineEventType =
  | 'run_started'
  | 'run_completed'
  | 'run_failed'
  | 'step_started'
  | 'step_succeeded'
  | 'step_failed'
  | 'step_skipped';

export interface PipelineClientEvent {
  type: PipelineEventType;
  runId: string;
  pipelineId: string;
  userId?: string;
  stepId?: string;
  error?: string;
}

export type StepStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface StepState {
  stepId: string;
  status: StepStatus;
}

export type RunStatus = 'running' | 'completed' | 'failed';

export interface PipelineRun {
  runId: string;
  pipelineId: string;
  status: RunStatus;
  steps: StepState[];
  error?: string;
}

const STEP_LABELS: Record<string, string> = {
  syncInitial: 'Syncing your data',
  syncIncrementally: 'Syncing your data',
  validateSyncResult: 'Validating sync',
  rebuildMetrics: 'Rebuilding metrics',
  mapProjects: 'Mapping projects',
};

export function getStepLabel(stepId: string): string {
  return STEP_LABELS[stepId] ?? stepId;
}

// How long (ms) to keep a completed/failed run visible before auto-dismissing
const DISMISS_DELAY = 4000;

export function usePipelineEvents() {
  const { on, off } = useSocket(auth.currentUser?.uid || '');
  const [runs, setRuns] = useState<Map<string, PipelineRun>>(new Map());

  useEffect(() => {
    const handler = (event: PipelineClientEvent) => {
      setRuns((prev) => {
        const next = new Map(prev);

        switch (event.type) {
          case 'run_started': {
            next.set(event.runId, {
              runId: event.runId,
              pipelineId: event.pipelineId,
              status: 'running',
              steps: [],
            });
            break;
          }

          case 'step_started': {
            const run = next.get(event.runId);
            if (run && event.stepId) {
              const steps = run.steps.filter((s) => s.stepId !== event.stepId);
              next.set(event.runId, {
                ...run,
                steps: [...steps, { stepId: event.stepId, status: 'running' }],
              });
            }
            break;
          }

          case 'step_succeeded':
          case 'step_failed':
          case 'step_skipped': {
            const run = next.get(event.runId);
            if (run && event.stepId) {
              const status: StepStatus =
                event.type === 'step_succeeded' ? 'succeeded' : event.type === 'step_failed' ? 'failed' : 'skipped';
              const steps = run.steps.map((s) => (s.stepId === event.stepId ? { ...s, status } : s));
              next.set(event.runId, { ...run, steps });
            }
            break;
          }

          case 'run_completed': {
            const run = next.get(event.runId);
            if (run) {
              next.set(event.runId, { ...run, status: 'completed' });
            }
            break;
          }

          case 'run_failed': {
            const run = next.get(event.runId);
            if (run) {
              next.set(event.runId, { ...run, status: 'failed', error: event.error });
            }
            break;
          }
        }

        return next;
      });

      // Auto-dismiss finished runs after a delay
      if (event.type === 'run_completed' || event.type === 'run_failed') {
        setTimeout(() => {
          setRuns((prev) => {
            const next = new Map(prev);
            next.delete(event.runId);
            return next;
          });
        }, DISMISS_DELAY);
      }
    };

    on('pipelineEvent', handler);
    return () => off('pipelineEvent', handler as any);
  }, [on, off]);

  const dismiss = (runId: string) => {
    setRuns((prev) => {
      const next = new Map(prev);
      next.delete(runId);
      return next;
    });
  };

  return { runs: Array.from(runs.values()), dismiss };
}
